import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, AlertCircle, Check, ChevronsUpDown, Search } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";

/**
 * Phase 101gd (Feb 2026) — Import rétroactif de factures papier.
 *
 * Cas d'usage Alain :
 * - ~4 factures/mois Chermignon depuis janvier 2026
 * - ~4 factures Oseo vers mars 2026
 * Toutes déjà envoyées papier au client, à saisir dans AgroManage pour le
 * suivi comptable (statistiques, cockpit, balance clients).
 *
 * Numérotation : la nouvelle référence F-YYYY-NNNNN est générée normalement
 * (séquence continue), et le n° papier est stocké dans `legacy_reference`.
 * Le PDF affiche « Remplace la facture papier n° X du DD/MM/YYYY ».
 *
 * Backdate autorisé : `issue_date` peut être antérieur à today (ex: 15/03/2026)
 * pour que la facture apparaisse dans le bon mois fiscal.
 */
export default function RetroactiveInvoiceDialog({ open, onClose, onCreated }) {
  const [distributors, setDistributors] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  // Phase 101gd — Popover ouvert pour la combobox client (persistance du filtre)
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState("");

  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    distributor_id: "",
    legacy_reference: "",
    legacy_date: "",         // date facture papier
    issue_date: "",          // date fiscale (défaut = legacy_date au blur)
    prices_include_vat: false,
    payment_terms: "",
    notes: "",
    additional_info: "",
    lines: [
      { description: "", quantity: 1, unit: "pcs", unit_price: 0, vat_rate: 2.6 },
    ],
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [d, p] = await Promise.all([
          api.get("/distributors"),
          api.get("/products"),
        ]);
        // Priorité aux clients "CLIENT" (revendeurs/traiteurs) puis distributeurs auto
        const sorted = [...d.data].sort((a, b) => {
          if (a.type !== b.type) return a.type === "CLIENT" ? -1 : 1;
          return (a.name || "").localeCompare(b.name || "");
        });
        setDistributors(sorted);
        setProducts(p.data);
      } catch (e) {
        toast.error("Erreur chargement clients/produits");
      }
    })();
  }, [open]);

  // Reset lorsqu'on ouvre
  useEffect(() => {
    if (open) {
      setForm({
        distributor_id: "",
        legacy_reference: "",
        legacy_date: "",
        issue_date: "",
        prices_include_vat: false,
        payment_terms: "",
        notes: "",
        additional_info: "",
        lines: [{ description: "", quantity: 1, unit: "pcs", unit_price: 0, vat_rate: 2.6 }],
      });
    }
  }, [open]);

  const addLine = () => setForm((f) => ({
    ...f,
    lines: [...f.lines, { description: "", quantity: 1, unit: "pcs", unit_price: 0, vat_rate: 2.6 }],
  }));
  const removeLine = (idx) => setForm((f) => ({
    ...f,
    lines: f.lines.filter((_, i) => i !== idx),
  }));
  const updateLine = (idx, patch) => setForm((f) => ({
    ...f,
    lines: f.lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
  }));
  const pickProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    const price = form.prices_include_vat
      ? (p.price_online ?? p.price_direct ?? 0)
      : (p.price_distributor ?? p.price_direct ?? 0);
    updateLine(idx, {
      description: `${p.name}${p.sku ? ` (${p.sku})` : ""}`,
      unit: p.unit || "pcs",
      unit_price: price || 0,
    });
  };

  // Auto-fill issue_date depuis legacy_date si vide
  const onLegacyDateBlur = () => {
    if (form.legacy_date && !form.issue_date) {
      setForm((f) => ({ ...f, issue_date: f.legacy_date }));
    }
  };

  const totals = useMemo(() => {
    let ht = 0;
    let vat = 0;
    for (const ln of form.lines) {
      const q = Number(ln.quantity) || 0;
      const up = Number(ln.unit_price) || 0;
      const rate = Number(ln.vat_rate) || 0;
      if (form.prices_include_vat) {
        const netTTC = q * up;
        const netHT = netTTC / (1 + rate / 100);
        ht += netHT;
        vat += netTTC - netHT;
      } else {
        const netHT = q * up;
        ht += netHT;
        vat += netHT * (rate / 100);
      }
    }
    const ttc = ht + vat;
    return { ht: Math.round(ht * 100) / 100, vat: Math.round(vat * 100) / 100, ttc: Math.round(ttc * 100) / 100 };
  }, [form.lines, form.prices_include_vat]);

  const canSubmit = form.distributor_id
    && form.legacy_reference.trim()
    && form.legacy_date
    && form.issue_date
    && form.lines.length > 0
    && form.lines.every((ln) => ln.description.trim() && Number(ln.quantity) > 0);

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Complétez tous les champs obligatoires");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        distributor_id: form.distributor_id,
        legacy_reference: form.legacy_reference.trim(),
        legacy_date: form.legacy_date,
        issue_date: form.issue_date,
        prices_include_vat: form.prices_include_vat,
        payment_terms: form.payment_terms || null,
        notes: form.notes,
        additional_info: form.additional_info,
        lines: form.lines.map((ln) => ({
          description: ln.description.trim(),
          quantity: Number(ln.quantity) || 0,
          unit: ln.unit || "pcs",
          unit_price: Number(ln.unit_price) || 0,
          vat_rate: Number(ln.vat_rate) || 0,
        })),
      };
      const { data } = await api.post("/invoices", payload);
      toast.success(`Facture ${data.reference} créée (remplace ${form.legacy_reference})`);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création facture");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="retro-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1C1A]">
            <FileText className="w-5 h-5 text-[#8C6B2F]" />
            Import facture papier (rétroactif)
          </DialogTitle>
          <DialogDescription className="text-[#575A57]">
            Saisie d'une facture déjà envoyée papier. Le n° papier est tracé,
            la nouvelle facture reçoit un numéro AgroManage continu (F-YYYY-NNNNN).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Client — Combobox avec recherche persistante
              Phase 101gd : remplace l'ancien Select Shadcn qui perdait le
              filtre à chaque interaction avec un autre champ (plainte Alain). */}
          <div className="space-y-1.5">
            <Label htmlFor="retro-client">Client *</Label>
            <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="retro-client"
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={clientPopoverOpen}
                  className="w-full justify-between font-normal"
                  data-testid="retro-invoice-client"
                >
                  {form.distributor_id
                    ? (() => {
                        const d = distributors.find((x) => x.id === form.distributor_id);
                        return d ? `${d.name}${d.type === "CLIENT" ? " · Client" : " · Auto"}` : "Sélectionner...";
                      })()
                    : <span className="text-[#A3A6A3]">Sélectionner le client…</span>}
                  <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[560px] p-0" align="start">
                <Command shouldFilter={true}>
                  <CommandInput
                    placeholder="Rechercher un client…"
                    value={clientFilter}
                    onValueChange={setClientFilter}
                    data-testid="retro-invoice-client-search"
                  />
                  <CommandList className="max-h-72">
                    <CommandEmpty>Aucun client correspondant</CommandEmpty>
                    <CommandGroup>
                      {distributors.map((d) => (
                        <CommandItem
                          key={d.id}
                          value={`${d.name} ${d.type === "CLIENT" ? "client" : "automatique"} ${d.id}`}
                          onSelect={() => {
                            setForm((f) => ({ ...f, distributor_id: d.id }));
                            setClientPopoverOpen(false);
                          }}
                          data-testid={`retro-invoice-client-opt-${d.id}`}
                        >
                          <Check className={`w-4 h-4 mr-2 ${form.distributor_id === d.id ? "opacity-100" : "opacity-0"}`} />
                          <span className="flex-1">{d.name}</span>
                          <span className={`text-[10px] ml-2 px-1.5 py-0.5 rounded ${d.type === "CLIENT" ? "bg-[#FBF4E9] text-[#8C6B2F]" : "bg-[#E8F2EC] text-[#1F4E3D]"}`}>
                            {d.type === "CLIENT" ? "Client" : "Auto"}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Références papier */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="retro-legacy-ref">N° facture papier *</Label>
              <Input
                id="retro-legacy-ref"
                placeholder="ex: FAC-2026-042"
                value={form.legacy_reference}
                onChange={(e) => setForm((f) => ({ ...f, legacy_reference: e.target.value }))}
                data-testid="retro-invoice-legacy-ref"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="retro-legacy-date">Date papier *</Label>
              <Input
                id="retro-legacy-date"
                type="date"
                max={today}
                value={form.legacy_date}
                onChange={(e) => setForm((f) => ({ ...f, legacy_date: e.target.value }))}
                onBlur={onLegacyDateBlur}
                data-testid="retro-invoice-legacy-date"
              />
            </div>
          </div>

          {/* Date fiscale + TVA */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="retro-issue-date">Date fiscale (émission) *</Label>
              <Input
                id="retro-issue-date"
                type="date"
                max={today}
                value={form.issue_date}
                onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))}
                data-testid="retro-invoice-issue-date"
              />
              <p className="text-[11px] text-[#A3A6A3]">Par défaut : identique à la date papier. Détermine le mois comptable.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="retro-vat-mode">Mode TVA</Label>
              <Select
                value={form.prices_include_vat ? "TTC" : "HT"}
                onValueChange={(v) => setForm((f) => ({ ...f, prices_include_vat: v === "TTC" }))}
              >
                <SelectTrigger id="retro-vat-mode" data-testid="retro-invoice-vat-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HT">Prix HT (B2B, revendeurs)</SelectItem>
                  <SelectItem value="TTC">Prix TTC-inclus (B2C, traiteur)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Lignes */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Lignes de facture *</Label>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="retro-invoice-add-line">
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            <div className="space-y-2">
              {form.lines.map((ln, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start p-3 rounded-lg border border-[#E5E5E0] bg-[#FBFBF7]" data-testid={`retro-invoice-line-${idx}`}>
                  <div className="col-span-5 space-y-1">
                    <Label className="text-[11px] text-[#A3A6A3]">Description</Label>
                    <Input
                      value={ln.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      placeholder="Description ou choisir un produit ↓"
                    />
                    <Select value="" onValueChange={(v) => pickProduct(idx, v)}>
                      <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Auto-remplir depuis un produit..." /></SelectTrigger>
                      <SelectContent>
                        {products.filter((p) => !p.archived).slice(0, 200).map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-[11px] text-[#A3A6A3]">Quantité</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={ln.quantity}
                      onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-[11px] text-[#A3A6A3]">Prix unit.</Label>
                    <Input
                      type="number"
                      step="0.05"
                      value={ln.unit_price}
                      onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-[11px] text-[#A3A6A3]">TVA %</Label>
                    <Select
                      value={String(ln.vat_rate)}
                      onValueChange={(v) => updateLine(idx, { vat_rate: Number(v) })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 %</SelectItem>
                        <SelectItem value="2.6">2.6 % (alimentaire)</SelectItem>
                        <SelectItem value="3.8">3.8 % (héberg.)</SelectItem>
                        <SelectItem value="8.1">8.1 % (standard)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 pt-6">
                    {form.lines.length > 1 && (
                      <Button
                        variant="ghost" size="icon"
                        className="text-[#C05C47]"
                        onClick={() => removeLine(idx)}
                        data-testid={`retro-invoice-remove-line-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="retro-notes">Notes internes (facultatif)</Label>
            <Textarea
              id="retro-notes"
              rows={2}
              placeholder="Notes non affichées au client"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="retro-invoice-notes"
            />
          </div>

          {/* Totaux */}
          <div className="rounded-lg bg-[#F5F9F6] border border-[#C5DBCF] p-3 space-y-1 text-sm">
            <div className="flex justify-between text-[#575A57]"><span>Total HT</span><span className="font-mono">{totals.ht.toFixed(2)} CHF</span></div>
            <div className="flex justify-between text-[#575A57]"><span>TVA</span><span className="font-mono">{totals.vat.toFixed(2)} CHF</span></div>
            <div className="flex justify-between text-[#1F4E3D] font-semibold pt-1 border-t border-[#C5DBCF]"><span>Total TTC</span><span className="font-mono">{totals.ttc.toFixed(2)} CHF</span></div>
          </div>

          <div className="rounded-lg bg-[#FBF4E9] border border-[#E8D3A8] p-3 text-[13px] text-[#8C6B2F] flex gap-2 items-start">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              La nouvelle facture sera créée en <strong>BROUILLON</strong>. Une fois validée, le PDF mentionnera automatiquement <em>« Remplace la facture papier n° {form.legacy_reference || "…"} du {form.legacy_date || "…"} »</em>. Pense à passer le statut à <strong>PAYÉE</strong> si le client a déjà réglé.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="retro-invoice-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || saving}
            className="bg-[#1F4E3D] hover:bg-[#16382C]"
            data-testid="retro-invoice-submit"
          >
            {saving ? "Création…" : "Créer la facture"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
